import React, { useEffect } from 'lib/react';
import {
  EVGrid, EVPaper,
} from 'lib/components';
import Typography from 'commonComponents/typography';
import { useTranslation } from 'lib/translation';
import Button from 'lib/components/button';
import { LEGACY_PORTAL_LINKS, CURRENCY_SYMBOL, ACCOUNT_LEVEL } from 'constants/AppConstants';
import { useDispatch } from 'lib/redux';
import { useSelector } from 'react-redux';
import { MANAGE_PAYMENT_OPTIONS_AMOUNT_UPDATES, MANAGE_PAYMENT_OPTIONS_AMOUNT_UPDATES_ERROR } from 'constants/ActionTypes';
import ComingSoon from 'commonComponents/comingSoonPage';
import TextInputField from 'commonComponents/textInputField';
import Banner from 'commonComponents/banner';
import { SUBMIT_PAYMENT_OPTION_ROUTE } from 'constants/RouteConstants';
import history from 'store/CreateHistory';
import ManagePaymentOptionsLoader from './ManagePaymentOptionsLoader';
import ManagePaymentOptionsStyles from './ManagePaymentOptionsStyles';
import { loadManagePaymentOptionsPage, closeManagePaymentsOption } from './ManagePaymentOptions.actions';
import SuspendPrepay from './components/SuspendPrepay.component';
import {
  CURRENCY_CAD, INPUT_AMOUNT_REGEX, MINIMUM_DEPOSIT_AMOUNT, VALID_AMOUNT_REGEX,
} from './ManagePaymentOptions.constants';
import MembershipLevelSummary from './components/MembershipLevelSummary.component';
import ManagePaymentOptionsUnvailable from './components/ManagePaymentOptionsUnavailable.component';

function ManagePaymentOptions() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = ManagePaymentOptionsStyles();

  useEffect(() => {
    dispatch(loadManagePaymentOptionsPage());
  }, []);

  const {
    accountSummary, myevRefreshManagePayment, nextPlanLevelDetails, paymentValues, loading, isTermsUser, managePaymentsBanner,
  } = useSelector(({ userData, featureFlag, managePaymentOptions }) => ({
    isTermsUser: userData.billingInformation?.isTermsUser,
    accountSummary: userData.accountSummary,
    myevRefreshManagePayment: featureFlag.myevRefreshManagePayment,
    ...managePaymentOptions,
  }));

  const handlePaymentsAlertClose = () => dispatch(closeManagePaymentsOption());

  const handleInputText = (e) => {
    const { target: { id, value } } = e;
    if (value.length === 0 || INPUT_AMOUNT_REGEX.test(value)) dispatch({ type: MANAGE_PAYMENT_OPTIONS_AMOUNT_UPDATES, id, value });
  };

  const handleOnClick = (id) => {
    if (!paymentValues[id] || parseFloat(paymentValues[id]).toFixed(2) < MINIMUM_DEPOSIT_AMOUNT || !VALID_AMOUNT_REGEX.test(paymentValues[id])) {
      dispatch({
        type: MANAGE_PAYMENT_OPTIONS_AMOUNT_UPDATES_ERROR,
        id: `${id}Error`,
        value: parseFloat(paymentValues[id]).toFixed(2) < MINIMUM_DEPOSIT_AMOUNT
          ? t('manage_payment__minimum_amount') : t('manage_payment__amount_error_message'),
      });
    } else history.push(`${SUBMIT_PAYMENT_OPTION_ROUTE}?type=${id}`);
  };

  if (!myevRefreshManagePayment) {
    return (
      <ComingSoon
        pageTitle={t('common__manage_payment_options')}
        redirectionLink={LEGACY_PORTAL_LINKS.paymentOptions}
      />
    );
  }
  if (loading) return (<ManagePaymentOptionsLoader />);

  if (accountSummary?.currencyTypeId === CURRENCY_CAD || isTermsUser) return <ManagePaymentOptionsUnvailable />;

  const paymentInputLine = (descText, handleInputChange, id) => (
    <EVGrid className={classes.prePaymentOptionBox}>
      <Typography variant="h4-400" fontColor="charcoal-900">
        {t(descText)}
      </Typography>
      <EVGrid className={classes.inputBox}>
        <Typography variant="h4-400" fontColor="charcoal-900">{CURRENCY_SYMBOL[1]}</Typography>
        <TextInputField
          inputValue={paymentValues[id]}
          inputId={id}
          handleChange={handleInputChange}
          variant="outlined"
          inputFieldProps={{
            placeholder: '100.00',
            inputProps: { maxLength: 20 },
            classes: { root: classes.textField },
            FormHelperTextProps: { classes: { error: classes.amountInputHelperText } },
          }}
          helperText={paymentValues[`${id}Error`]}
        />
      </EVGrid>
    </EVGrid>
  );
  const buttonComponent = (buttonName, onClick, id) => (
    <Button
      id={`${id}-btn`}
      disabled={paymentValues[`${id}Error`]}
      variant="contained"
      color="primary"
      className={classes.button}
      onClick={() => onClick(id)}
    >
      {t(buttonName)}
    </Button>
  );
  return (
    <>
      {managePaymentsBanner?.open && (
      <Banner
        severity={managePaymentsBanner.severity}
        handleAlertClose={handlePaymentsAlertClose}
        message={managePaymentsBanner.message}
      />
      )}
      <EVGrid className={classes.managePaymentContainer}>
        <EVGrid className={classes.prePaymentBox}>
          {accountSummary?.membershipLevel !== ACCOUNT_LEVEL.CUSTOM && (
            <EVPaper className={classes.prePaymentPaper}>
              <EVGrid className={classes.prePaymentPaperContent}>
                <Typography variant="h3-600" fontColor="ocean-700">
                  {t('manage_payment__update_monthly_prepayment')}
                </Typography>
                <EVGrid className={classes.prePaymentOptionBox}>
                  <Typography variant="h4-400" fontColor="charcoal-700">
                    {t('manage_payment__current_monthly_prepayment')}
                  </Typography>
                  <Typography variant="h4-400" fontColor="charcoal-700">
                    {CURRENCY_SYMBOL[1]}
                    {accountSummary?.currentMonthlyPrepaymentAmount || '0.00'}
                  </Typography>
                </EVGrid>
                {paymentInputLine('manage_payment__new_monthly_prepayment', handleInputText, 'MonthlyPayment')}
                {buttonComponent('Update', handleOnClick, 'MonthlyPayment')}
              </EVGrid>
              {accountSummary?.suspendMonthlyPrepay && <SuspendPrepay />}
            </EVPaper>
          )}
          <EVPaper className={classes.prePaymentPaper}>
            <EVGrid className={classes.prePaymentPaperContent}>
              <Typography variant="h3-600" fontColor="ocean-700">{t('manage_payment__make_prepayment')}</Typography>
              {paymentInputLine('manage_payment__prepayment_amount', handleInputText, 'PrePayment')}
              {buttonComponent('manage_payment__deposit_now', handleOnClick, 'PrePayment')}
            </EVGrid>
          </EVPaper>
        </EVGrid>
        {!(accountSummary?.currencyTypeId === CURRENCY_CAD || isTermsUser) && (
        <MembershipLevelSummary
          accountSummary={accountSummary}
          nextPlanLevelDetails={nextPlanLevelDetails}
        />
        )}
      </EVGrid>
    </>
  );
}

export default ManagePaymentOptions;
